export const COOGO_ORCHESTRATION_PLAN_V1_VERSION =
  "coogo.orchestration-plan.v1" as const
export const COOGO_ORCHESTRATION_PLAN_V2_VERSION =
  "coogo.orchestration-plan.v2" as const
/** Historical writer alias. New v2 writers must import the explicit v2 constant. */
export const COOGO_ORCHESTRATION_PLAN_VERSION =
  COOGO_ORCHESTRATION_PLAN_V1_VERSION

export const COOGO_EXECUTION_EVENT_V1_VERSION =
  "coogo.execution-event.v1" as const
export const COOGO_EXECUTION_EVENT_V2_VERSION =
  "coogo.execution-event.v2" as const
/** Historical writer alias. New v2 writers must import the explicit v2 constant. */
export const COOGO_EXECUTION_EVENT_VERSION = COOGO_EXECUTION_EVENT_V1_VERSION

export type CoogoOrchestrationPlanVersion =
  | typeof COOGO_ORCHESTRATION_PLAN_V1_VERSION
  | typeof COOGO_ORCHESTRATION_PLAN_V2_VERSION

export type CoogoExecutionEventVersion =
  | typeof COOGO_EXECUTION_EVENT_V1_VERSION
  | typeof COOGO_EXECUTION_EVENT_V2_VERSION

export type CoogoOrchestrationTrigger =
  | "chat"
  | "webmcp"
  | "api"
  | "automation"
  | "studio-ui"

export type CoogoOrchestrationNodeKind =
  | "llm"
  | "rag"
  | "tool"
  | "actionflow"
  | "human"
  | "gate"
  | "readback"

export type CoogoOrchestrationDependencyCondition = "completed" | "failed"

export interface CoogoConditionalDependency {
  nodeId: string
  when: CoogoOrchestrationDependencyCondition
}

export type CoogoOrchestrationDependency = string | CoogoConditionalDependency

export interface NormalizedCoogoDependency {
  nodeId: string
  when: CoogoOrchestrationDependencyCondition
}

export interface CoogoHumanGateAdmission {
  confirmationId: string
  expiresAt: string
  principalBinding: "authenticated-owner"
  sessionBinding: "verified-jwt-session"
  target: {
    kind: "studio-page"
    projectId: string
    pageId: string
  }
  duplicatePolicy: "reject"
  durability: "durable" | "session-only"
  confirmationPhrase?: string
}

export interface CoogoOrchestrationNodePolicy {
  authority: "browser" | "studio" | "server" | "runtime" | "human"
  readOnly: boolean
  confirmation: "none" | "user" | "typed" | "policy"
  retry: "never" | "safe"
  timeoutMs: number
  idempotencyKey?: string
}

export interface CoogoRagEvidenceScope {
  releaseId: string
  subjectId: string
  projection?: string
  provenanceRequired: true
}

interface CoogoOrchestrationNodeFields {
  id: string
  kind: CoogoOrchestrationNodeKind
  label?: string
  operation?: {
    name: string
    params?: Record<string, unknown>
  }
  input?: Record<string, unknown>
  evidenceScope?: CoogoRagEvidenceScope
  policy: CoogoOrchestrationNodePolicy
}

/** Historical v1 node: dependencies remain string-only. */
export interface CoogoOrchestrationNode extends CoogoOrchestrationNodeFields {
  dependsOn?: string[]
}

export type CoogoOrchestrationNodeV1 = CoogoOrchestrationNode

/** V2 writer node: conditional dependency objects are admitted explicitly. */
export interface CoogoOrchestrationNodeV2
  extends CoogoOrchestrationNodeFields {
  dependsOn?: CoogoOrchestrationDependency[]
  humanGate?: CoogoHumanGateAdmission
}

interface CoogoOrchestrationPlanFields {
  planId: string
  label?: string
  runId: string
  /** Parent transport run used only for correlation; runId remains unique per plan. */
  parentRunId?: string
  tenantId?: string
  projectId: string
  trigger: CoogoOrchestrationTrigger
  createdAt: string
}

/** Historical v1 plan and generic legacy-writer type. */
export interface CoogoOrchestrationPlan extends CoogoOrchestrationPlanFields {
  schemaVersion: typeof COOGO_ORCHESTRATION_PLAN_V1_VERSION
  nodes: CoogoOrchestrationNode[]
}

export type CoogoOrchestrationPlanV1 = CoogoOrchestrationPlan

export interface CoogoOrchestrationPlanV2
  extends CoogoOrchestrationPlanFields {
  schemaVersion: typeof COOGO_ORCHESTRATION_PLAN_V2_VERSION
  nodes: CoogoOrchestrationNodeV2[]
}

export type CoogoReadableOrchestrationPlan =
  | CoogoOrchestrationPlanV1
  | CoogoOrchestrationPlanV2

export type CoogoExecutionEventTypeV1 =
  | "plan.accepted"
  | "plan.suspended"
  | "plan.resumed"
  | "plan.completed"
  | "plan.failed"
  | "plan.cancelled"
  | "node.started"
  | "node.completed"
  | "node.failed"

export type CoogoExecutionEventTypeV2 =
  | CoogoExecutionEventTypeV1
  | "node.skipped"
  | "node.cancelled"

/** Historical generic event type retained for v1 writers. */
export type CoogoExecutionEventType = CoogoExecutionEventTypeV1

interface CoogoExecutionEventFields {
  eventId: string
  planId: string
  runId: string
  projectId: string
  sequence: number
  occurredAt: string
  nodeId?: string
  data?: Record<string, unknown>
}

/** Historical v1 event and generic legacy-writer type. */
export interface CoogoExecutionEvent extends CoogoExecutionEventFields {
  schemaVersion: typeof COOGO_EXECUTION_EVENT_V1_VERSION
  type: CoogoExecutionEventTypeV1
}

export type CoogoExecutionEventV1 = CoogoExecutionEvent

export interface CoogoExecutionEventV2 extends CoogoExecutionEventFields {
  schemaVersion: typeof COOGO_EXECUTION_EVENT_V2_VERSION
  type: CoogoExecutionEventTypeV2
}

export type CoogoReadableExecutionEvent =
  | CoogoExecutionEventV1
  | CoogoExecutionEventV2

export interface CoogoContractValidationResult {
  valid: boolean
  errors: string[]
}

const PLAN_EVENT_TYPES = new Set<CoogoExecutionEventTypeV1>([
  "plan.accepted",
  "plan.suspended",
  "plan.resumed",
  "plan.completed",
  "plan.failed",
  "plan.cancelled",
])
const NODE_EVENT_TYPES_V1 = new Set<CoogoExecutionEventTypeV1>([
  "node.started",
  "node.completed",
  "node.failed",
])
const NODE_EVENT_TYPES_V2 = new Set<CoogoExecutionEventTypeV2>([
  ...NODE_EVENT_TYPES_V1,
  "node.skipped",
  "node.cancelled",
])
const TRIGGERS = new Set<CoogoOrchestrationTrigger>([
  "chat",
  "webmcp",
  "api",
  "automation",
  "studio-ui",
])
const NODE_KINDS = new Set<CoogoOrchestrationNodeKind>([
  "llm",
  "rag",
  "tool",
  "actionflow",
  "human",
  "gate",
  "readback",
])
const AUTHORITIES = new Set<CoogoOrchestrationNodePolicy["authority"]>([
  "browser",
  "studio",
  "server",
  "runtime",
  "human",
])
const CONFIRMATION_POLICIES = new Set<
  CoogoOrchestrationNodePolicy["confirmation"]
>(["none", "user", "typed", "policy"])
const RETRY_POLICIES = new Set<CoogoOrchestrationNodePolicy["retry"]>([
  "never",
  "safe",
])

export function parseCoogoDependency(
  dependency: unknown,
): NormalizedCoogoDependency | undefined {
  if (typeof dependency === "string") {
    return dependency.trim()
      ? { nodeId: dependency, when: "completed" }
      : undefined
  }
  if (!isRecord(dependency)) return undefined
  if (typeof dependency.nodeId !== "string" || !dependency.nodeId.trim()) {
    return undefined
  }
  if (dependency.when !== "completed" && dependency.when !== "failed") {
    return undefined
  }
  return { nodeId: dependency.nodeId, when: dependency.when }
}

export function normalizedDependencies(
  node: Pick<CoogoOrchestrationNodeV2, "dependsOn">,
): NormalizedCoogoDependency[] {
  return (node.dependsOn ?? [])
    .map(parseCoogoDependency)
    .filter((dependency): dependency is NormalizedCoogoDependency =>
      Boolean(dependency),
    )
}

export function validateCoogoOrchestrationPlan(
  plan: CoogoReadableOrchestrationPlan,
): CoogoContractValidationResult {
  const errors: string[] = []
  const isV1 = plan.schemaVersion === COOGO_ORCHESTRATION_PLAN_V1_VERSION
  const isV2 = plan.schemaVersion === COOGO_ORCHESTRATION_PLAN_V2_VERSION
  if (!isV1 && !isV2) {
    errors.push(
      `schemaVersion must be ${COOGO_ORCHESTRATION_PLAN_V1_VERSION} or ${COOGO_ORCHESTRATION_PLAN_V2_VERSION}`,
    )
  }
  for (const [field, value] of [
    ["planId", plan.planId],
    ["runId", plan.runId],
    ["projectId", plan.projectId],
    ["createdAt", plan.createdAt],
  ] as const) {
    if (!value?.trim()) errors.push(`${field} is required`)
  }
  if (plan.parentRunId !== undefined && !plan.parentRunId.trim()) {
    errors.push("parentRunId must be non-empty when provided")
  }
  if (plan.label !== undefined && !plan.label.trim()) {
    errors.push("label must be non-empty when provided")
  }
  if (!TRIGGERS.has(plan.trigger)) errors.push(`invalid trigger: ${plan.trigger}`)
  if (plan.createdAt && Number.isNaN(Date.parse(plan.createdAt))) {
    errors.push("createdAt must be an ISO-compatible timestamp")
  }
  if (!Array.isArray(plan.nodes) || plan.nodes.length === 0) {
    errors.push("nodes must contain at least one node")
    return { valid: false, errors }
  }

  const nodeIds = new Set<string>()
  const humanGateConfirmationIds = new Set<string>()
  const humanGateNodeIds: string[] = []
  for (const node of plan.nodes) {
    if (!node.id?.trim()) errors.push("node id is required")
    if (nodeIds.has(node.id)) errors.push(`duplicate node id: ${node.id}`)
    nodeIds.add(node.id)
    if (!NODE_KINDS.has(node.kind)) errors.push(`${node.id}: invalid node kind`)
    if (
      (node.kind === "tool" ||
        node.kind === "actionflow" ||
        node.kind === "readback") &&
      !node.operation?.name?.trim()
    ) {
      errors.push(`${node.id}: ${node.kind} nodes require an operation name`)
    }
    const policy = node.policy
    if (!policy || typeof policy !== "object") {
      errors.push(`${node.id}: policy is required`)
      continue
    }
    if (!AUTHORITIES.has(policy.authority)) {
      errors.push(`${node.id}: invalid execution authority`)
    }
    if (!CONFIRMATION_POLICIES.has(policy.confirmation)) {
      errors.push(`${node.id}: invalid confirmation policy`)
    }
    if (!RETRY_POLICIES.has(policy.retry)) {
      errors.push(`${node.id}: invalid retry policy`)
    }
    if (!Number.isFinite(policy.timeoutMs) || policy.timeoutMs <= 0) {
      errors.push(`${node.id}: timeoutMs must be positive`)
    }
    if (!policy.readOnly) {
      if (!policy.idempotencyKey?.trim()) {
        errors.push(`${node.id}: mutating nodes require an idempotencyKey`)
      }
      if (policy.confirmation === "none") {
        errors.push(
          `${node.id}: mutating nodes require confirmation or policy approval`,
        )
      }
    }
    if (node.kind === "llm") {
      if (policy.authority !== "server") {
        errors.push(`${node.id}: llm nodes must use server authority`)
      }
      if (!policy.readOnly)
        errors.push(`${node.id}: llm nodes cannot mutate state directly`)
    }
    if (node.kind === "rag") {
      if (policy.authority !== "server" || !policy.readOnly) {
        errors.push(
          `${node.id}: rag nodes must be read-only with server authority`,
        )
      }
      const scope = node.evidenceScope
      if (
        !scope?.releaseId?.trim() ||
        !scope.subjectId?.trim() ||
        scope.provenanceRequired !== true
      ) {
        errors.push(
          `${node.id}: rag nodes require releaseId, subjectId, and provenanceRequired=true`,
        )
      }
    }
    const rawNode = node as unknown as Record<string, unknown>
    const hasHumanGate = Object.prototype.hasOwnProperty.call(
      rawNode,
      "humanGate",
    )
    if (isV1 && hasHumanGate) {
      errors.push(`${node.id}: humanGate is not allowed in v1 plans`)
    } else if (isV2) {
      const isHumanGate = node.kind === "gate" && policy.authority === "human"
      if (isHumanGate) {
        humanGateNodeIds.push(node.id)
        const confirmationId = (
          node as CoogoOrchestrationNodeV2
        ).humanGate?.confirmationId
        if (
          confirmationId?.trim() &&
          humanGateConfirmationIds.has(confirmationId)
        ) {
          errors.push(`${node.id}: humanGate.confirmationId must be unique`)
        } else if (confirmationId?.trim()) {
          humanGateConfirmationIds.add(confirmationId)
        }
      }
      if (!isHumanGate && hasHumanGate) {
        errors.push(
          `${node.id}: humanGate is allowed only on a human-authority gate node`,
        )
      } else if (isHumanGate && !hasHumanGate) {
        errors.push(
          `${node.id}: human-authority gate requires humanGate admission`,
        )
      } else if (isHumanGate) {
        validateHumanGateAdmission(
          rawNode.humanGate,
          node.id,
          plan,
          policy.confirmation,
          errors,
        )
      }
    }
  }
  for (const node of plan.nodes) {
    const conditions = new Map<string, Set<CoogoOrchestrationDependencyCondition>>()
    for (const rawDependency of node.dependsOn ?? []) {
      if (isV1 && typeof rawDependency !== "string") {
        errors.push(`${node.id}: v1 dependencies must be node id strings`)
        continue
      }
      if (isV2 && isRecord(rawDependency)) {
        if (
          typeof rawDependency.nodeId !== "string" ||
          !rawDependency.nodeId.trim()
        ) {
          errors.push(`${node.id}: dependency nodeId is required`)
          continue
        }
        if (
          rawDependency.when !== "completed" &&
          rawDependency.when !== "failed"
        ) {
          errors.push(
            `${node.id}: invalid dependency condition: ${String(rawDependency.when)}`,
          )
          continue
        }
      }
      const dependency =
        isV1 && typeof rawDependency === "string"
          ? { nodeId: rawDependency, when: "completed" as const }
          : parseCoogoDependency(rawDependency)
      if (!dependency) {
        if (isV2) errors.push(`${node.id}: dependency nodeId is required`)
        continue
      }
      if (dependency.nodeId === node.id) {
        errors.push(`${node.id}: node cannot depend on itself`)
      } else if (!nodeIds.has(dependency.nodeId)) {
        errors.push(`${node.id}: unknown dependency ${dependency.nodeId}`)
      }
      const expected = conditions.get(dependency.nodeId) ?? new Set()
      expected.add(dependency.when)
      conditions.set(dependency.nodeId, expected)
    }
    for (const [dependencyId, expected] of conditions) {
      if (expected.has("completed") && expected.has("failed")) {
        errors.push(
          `${node.id}: dependency ${dependencyId} cannot require both completed and failed`,
        )
      }
    }
  }
  if (hasDependencyCycle(plan.nodes)) {
    errors.push("node dependencies must be acyclic")
  }
  for (let index = 1; index < humanGateNodeIds.length; index += 1) {
    const previousGateId = humanGateNodeIds[index - 1]
    const gateId = humanGateNodeIds[index]
    if (!dependsTransitivelyOnCompleted(plan.nodes, gateId, previousGateId)) {
      errors.push(
        `${gateId}: sequential human gate must depend on completed ${previousGateId}`,
      )
    }
  }
  return { valid: errors.length === 0, errors }
}

function dependsTransitivelyOnCompleted(
  nodes: readonly (CoogoOrchestrationNodeV1 | CoogoOrchestrationNodeV2)[],
  nodeId: string,
  expectedAncestorId: string,
  seen = new Set<string>(),
): boolean {
  if (seen.has(nodeId)) return false
  seen.add(nodeId)
  const node = nodes.find((candidate) => candidate.id === nodeId)
  if (!node) return false
  for (const rawDependency of node.dependsOn ?? []) {
    const dependency = parseCoogoDependency(rawDependency)
    if (!dependency || dependency.when !== "completed") continue
    if (dependency.nodeId === expectedAncestorId) return true
    if (
      dependsTransitivelyOnCompleted(
        nodes,
        dependency.nodeId,
        expectedAncestorId,
        seen,
      )
    ) {
      return true
    }
  }
  return false
}

export function validateCoogoExecutionEvent(
  event: CoogoReadableExecutionEvent,
): CoogoContractValidationResult {
  const errors: string[] = []
  const isV1 = event.schemaVersion === COOGO_EXECUTION_EVENT_V1_VERSION
  const isV2 = event.schemaVersion === COOGO_EXECUTION_EVENT_V2_VERSION
  if (!isV1 && !isV2) {
    errors.push(
      `schemaVersion must be ${COOGO_EXECUTION_EVENT_V1_VERSION} or ${COOGO_EXECUTION_EVENT_V2_VERSION}`,
    )
  }
  for (const [field, value] of [
    ["eventId", event.eventId],
    ["planId", event.planId],
    ["runId", event.runId],
    ["projectId", event.projectId],
    ["occurredAt", event.occurredAt],
  ] as const) {
    if (!value?.trim()) errors.push(`${field} is required`)
  }
  if (!Number.isInteger(event.sequence) || event.sequence < 0) {
    errors.push("sequence must be a non-negative integer")
  }
  const nodeTypes = isV2 ? NODE_EVENT_TYPES_V2 : NODE_EVENT_TYPES_V1
  const isPlanEvent = PLAN_EVENT_TYPES.has(event.type as CoogoExecutionEventTypeV1)
  const isNodeEvent = nodeTypes.has(event.type as CoogoExecutionEventTypeV2)
  if (!isPlanEvent && !isNodeEvent) {
    errors.push(`invalid execution event type: ${event.type}`)
  } else if (isNodeEvent && !event.nodeId?.trim()) {
    errors.push(`${event.type} requires nodeId`)
  }
  return { valid: errors.length === 0, errors }
}

function hasDependencyCycle(
  nodes: readonly (CoogoOrchestrationNodeV1 | CoogoOrchestrationNodeV2)[],
): boolean {
  const dependencies = new Map(
    nodes.map((node) => [
      node.id,
      (node.dependsOn ?? [])
        .map(parseCoogoDependency)
        .filter(
          (dependency): dependency is NormalizedCoogoDependency =>
            Boolean(dependency),
        ),
    ]),
  )
  const visiting = new Set<string>()
  const visited = new Set<string>()
  const visit = (nodeId: string): boolean => {
    if (visiting.has(nodeId)) return true
    if (visited.has(nodeId)) return false
    visiting.add(nodeId)
    for (const dependency of dependencies.get(nodeId) ?? []) {
      if (dependencies.has(dependency.nodeId) && visit(dependency.nodeId)) {
        return true
      }
    }
    visiting.delete(nodeId)
    visited.add(nodeId)
    return false
  }
  return nodes.some((node) => visit(node.id))
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function validateHumanGateAdmission(
  value: unknown,
  nodeId: string,
  plan: CoogoReadableOrchestrationPlan,
  confirmation: CoogoOrchestrationNodePolicy["confirmation"],
  errors: string[],
): void {
  if (!isRecord(value)) {
    errors.push(`${nodeId}: humanGate admission must be an object`)
    return
  }
  const allowed = new Set([
    "confirmationId",
    "expiresAt",
    "principalBinding",
    "sessionBinding",
    "target",
    "duplicatePolicy",
    "durability",
    "confirmationPhrase",
  ])
  for (const key of Object.keys(value)) {
    if (!allowed.has(key)) {
      errors.push(`${nodeId}: humanGate contains unknown field: ${key}`)
    }
  }
  if (
    typeof value.confirmationId !== "string" ||
    !value.confirmationId.trim()
  ) {
    errors.push(`${nodeId}: humanGate.confirmationId is required`)
  }
  const expiresAt =
    typeof value.expiresAt === "string" ? Date.parse(value.expiresAt) : NaN
  const createdAt = Date.parse(plan.createdAt)
  if (
    !Number.isFinite(expiresAt) ||
    !Number.isFinite(createdAt) ||
    expiresAt <= createdAt
  ) {
    errors.push(
      `${nodeId}: humanGate.expiresAt must be a valid timestamp after plan creation`,
    )
  }
  if (value.principalBinding !== "authenticated-owner") {
    errors.push(
      `${nodeId}: humanGate.principalBinding must be authenticated-owner`,
    )
  }
  if (value.sessionBinding !== "verified-jwt-session") {
    errors.push(
      `${nodeId}: humanGate.sessionBinding must be verified-jwt-session`,
    )
  }
  if (value.duplicatePolicy !== "reject") {
    errors.push(`${nodeId}: humanGate.duplicatePolicy must be reject`)
  }
  if (value.durability !== "durable") {
    errors.push(`${nodeId}: humanGate.durability must be durable`)
  }
  if (confirmation !== "typed") {
    errors.push(`${nodeId}: human gate confirmation must be typed`)
  }
  if (
    typeof value.confirmationPhrase !== "string" ||
    !value.confirmationPhrase.trim()
  ) {
    errors.push(`${nodeId}: typed human gate requires confirmationPhrase`)
  }
  if (!isRecord(value.target)) {
    errors.push(`${nodeId}: humanGate.target must be an object`)
    return
  }
  const targetAllowed = new Set(["kind", "projectId", "pageId"])
  for (const key of Object.keys(value.target)) {
    if (!targetAllowed.has(key)) {
      errors.push(`${nodeId}: humanGate.target contains unknown field: ${key}`)
    }
  }
  if (value.target.kind !== "studio-page") {
    errors.push(`${nodeId}: humanGate.target.kind must be studio-page`)
  }
  if (value.target.projectId !== plan.projectId) {
    errors.push(
      `${nodeId}: humanGate.target.projectId must match plan projectId`,
    )
  }
  if (typeof value.target.pageId !== "string" || !value.target.pageId.trim()) {
    errors.push(`${nodeId}: humanGate.target.pageId is required`)
  }
}
